/**
 * GameScreenView — root presentational component for the Celestia in-match
 * screen (multiplayer, server-authoritative).
 *
 * Faithful port of the canonical wireframe layout:
 *   docs/wireframes/celestia/03-screen.html
 *
 * Architecture: mobile-first 412px `.canvas` → sticky `.bhdr` (BoardHeader) →
 * non-clickable phase stack `.stack` (PhaseStack) → fixed bottom dock `.dock`
 * (ActionDock + HandPanel). Overlays: ValidationToast, overflow .menu,
 * end-of-game panel.
 *
 * Sets `--kind` on the canvas so the current phase card and dock header tint
 * use the correct accent colour (decision→captain-ring, reaction→pulse,
 * neutral→selection) — mirrors the wireframe script `KCOL[kind]`.
 *
 * Takes ALL behaviour from `props.actions`; takes ALL visuals from the
 * wireframe CSS classes. No state except the small UI toggles (menu open,
 * hand panel managed inside HandPanel, header detail managed inside
 * BoardHeader).
 */

import { useState } from "react";
import { CelestiaIconSprite } from "../components/CelestiaIconSprite";
import { phaseAccent } from "../screen-constants";
import { ActionDock } from "./ActionDock";
import { BoardHeader } from "./BoardHeader";
import { HandPanel } from "./HandPanel";
import { PhaseStack } from "./PhaseStack";
import type { GameScreenViewProps } from "./types";

export function GameScreenView({
  viewerState,
  myPlayerId,
  connectionStatus,
  i18n,
  prefs,
  actions,
  onLeave,
  lastError,
  onClearError,
}: GameScreenViewProps) {
  const { t } = i18n;
  const [menuOpen, setMenuOpen] = useState(false);

  const phaseId = viewerState.currentPhase.phase;
  const accent = phaseAccent(phaseId);
  const me = viewerState.players.find((p) => p.playerId === myPlayerId) ?? null;
  const isGameOver = viewerState.winners !== null;

  return (
    <div
      data-celestia-root=""
      data-testid="game-screen"
      data-text-scale={prefs.textScale}
      data-dyslexia-font={prefs.dyslexiaFont ? "true" : "false"}
      style={{
        minHeight: "100vh",
        background: "var(--cn-celestia-bg)",
        color: "var(--cn-celestia-card-label)",
      }}
    >
      {/* Icon sprite — rendered once at root, referenced via <use href="#ic-…"> */}
      <CelestiaIconSprite />

      <div
        className="canvas"
        style={{ "--kind": accent } as React.CSSProperties}
      >
        {/* Sticky header */}
        <BoardHeader
          viewerState={viewerState}
          myPlayerId={myPlayerId}
          connectionStatus={connectionStatus}
          i18n={i18n}
          onMenuOpen={() => setMenuOpen((v) => !v)}
        />

        {/* Non-clickable phase record stack */}
        <PhaseStack viewerState={viewerState} myPlayerId={myPlayerId} />

        {/* Validation error toast */}
        {lastError !== null && (
          <div
            data-testid="celestia-error-banner"
            role="alert"
            aria-live="assertive"
            style={{
              position: "fixed",
              top: 60,
              left: 0,
              right: 0,
              zIndex: 200,
              margin: "0 auto",
              maxWidth: 412,
              padding: "10px 16px",
              background: "var(--cn-celestia-deadline-ring)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <span>{t(lastError) !== lastError ? t(lastError) : lastError}</span>
            <button
              type="button"
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                padding: 0,
              }}
              onClick={onClearError}
              aria-label="닫기"
            >
              ✕
            </button>
          </div>
        )}

        {/* Fixed bottom dock */}
        <aside className="dock" aria-label="하단 액션">
          <ActionDock
            viewerState={viewerState}
            myPlayerId={myPlayerId}
            actions={actions}
            i18n={i18n}
          />
          <HandPanel me={me} i18n={i18n} />
        </aside>
      </div>

      {/* Overflow menu (⋯ button) */}
      <div
        className={menuOpen ? "menu open" : "menu"}
        role="menu"
        aria-label="추가 액션"
        style={{ top: 48 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="mi" role="menuitem" type="button" onClick={onLeave}>
          방 나가기 (Leave) <span className="tag">퇴장</span>
        </button>
        <button type="button" className="mi" role="menuitem">
          기권 (Resign) <span className="tag">구현 예정</span>
        </button>
        <button type="button" className="mi" role="menuitem">
          규칙 보기 <span className="tag">구현 예정</span>
        </button>
      </div>

      {/* Click-away to close menu */}
      {menuOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 205,
            background: "transparent",
          }}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* End-of-game overlay */}
      {isGameOver && (
        <div
          data-testid="celestia-end-of-game"
          role="dialog"
          aria-label="게임 종료"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            background: "rgba(0,0,0,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            style={{
              background: "var(--cn-celestia-bottom-sheet-bg)",
              border: "1px solid var(--line)",
              borderRadius: "var(--cn-radius-lg)",
              boxShadow: "var(--cn-elev-3)",
              padding: "24px 20px",
              maxWidth: 380,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: "var(--cn-celestia-score-winner)",
              }}
            >
              🏆 게임 종료
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--cn-celestia-card-label)",
              }}
            >
              {viewerState.winners !== null && viewerState.winners.length > 0
                ? (() => {
                    const winnerNames = viewerState.winners
                      .map((wId) => {
                        const p = viewerState.players.find(
                          (pl) => pl.playerId === wId,
                        );
                        return p?.displayName ?? wId;
                      })
                      .join(", ");
                    return viewerState.winners.length > 1
                      ? `공동 우승: ${winnerNames}`
                      : `우승자: ${winnerNames}`;
                  })()
                : "게임이 종료되었습니다."}
            </div>
            <button
              type="button"
              style={{
                background: "var(--cn-celestia-selection)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--cn-radius-sm)",
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 800,
                cursor: "pointer",
              }}
              onClick={onLeave}
            >
              {t("btn_back_to_lobby")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

import type React from "react";
